"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { useId } from "react";
import { RegisterOptions, useFormContext, useFormState } from "react-hook-form";
import styles from "./CheckboxGroupField.module.scss";
import { CheckboxInput } from "../../input/CheckboxInput/CheckboxInput";
import clsx from "clsx";

type Option = {
	label: string;
	value: string;
};

export interface CheckboxGroupFieldProps {
	name: string;
	label: string;
	hideLabel?: boolean;
	options?: Option[];
	className?: string;
	help?: string;
	registerOptions?: RegisterOptions;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
}

export const CheckboxGroupField = ({
	name,
	label,
	hideLabel,
	options,
	className,
	help,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
}: CheckboxGroupFieldProps) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { register } = useFormContext();
	const { errors } = useFormState();

	return (
		<FieldWrapper
			{...{
				name,
				label,
				hideLabel,
				required: registerOptions?.required as boolean,
				labelId: id,
				className,
				errorId,
			}}
			slotAfterLabel={
				help && (
					<span
						className={styles["checkbox-group__help"]}
						id={helpId}
					>
						{help}
					</span>
				)
			}
		>
			<div className={clsx(styles["checkbox-group-wrapper"])}>
				<div
					className={clsx(
						styles["checkbox-group"],
						hideLabel && styles["checkbox-group--hidden-label"],
					)}
					aria-labelledby={id}
				>
					{options?.map(checkbox => (
						<CheckboxInput
							value={checkbox.value}
							key={checkbox.value}
							label={checkbox.label}
							aria-describedby={
								[help ? helpId : "", errors[name] ? errorId : "", ariaDescribedBy]
									.filter(v => v)
									.join(" ") || undefined
							}
							{...register(name, registerOptions)}
							{...inputProps}
						/>
					))}
				</div>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
